import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUSINESS_CATEGORIES } from "@/lib/categories";
import { PhotoUpload } from "@/components/PhotoUpload";
import { DealsEditor } from "@/components/DealsEditor";

const withKeys = (arr) => (arr || []).map((x) => ({ _key: crypto.randomUUID(), ...x }));
const EMPTY = { name: "", category: "restaurants", description: "", city: "", address: "", phone: "", website: "", hours: "",
  price_range: "", reservation_required: false, reservation_note: "", image_url: "", schedule: [], daily_update: "", deals: [] };
const cls = "rounded-xl bg-[#0B0F19] border-[#26334D] focus-visible:ring-[#FF3B6B]";

export function BusinessForm({ initial, onSubmit, busy }) {
  const [f, setF] = useState(() => ({ ...EMPTY, ...initial, schedule: withKeys(initial?.schedule), deals: withKeys(initial?.deals) }));
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setSched = (i, k, v) => setF({ ...f, schedule: f.schedule.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });

  const text = (k, label, ph, req) => (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-sm">{label}{req && " *"}</Label>
      <Input data-testid={`biz-${k}-input`} required={req} placeholder={ph} value={f[k]} onChange={set(k)} className={`h-11 ${cls}`} />
    </div>
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        {text("name", "Business name", "Sunset Comedy Club", true)}
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-sm">Category *</Label>
          <Select value={f.category} onValueChange={set("category")}>
            <SelectTrigger data-testid="biz-category-select" className={`h-11 ${cls}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#151C2C] border-[#26334D] text-white">
              {BUSINESS_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`biz-category-${c.id}`}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {text("city", "City, State/Country", "Miami, FL", true)}
        {text("address", "Street address", "123 Ocean Dr, Miami, FL", true)}
        {text("phone", "Phone", "+1 305 555 0123")}
        {text("website", "Website / booking link", "https://")}
        {text("hours", "Operating hours", "Mon-Fri 5pm-12am; Sat-Sun 2pm-2am")}
        {text("price_range", "Price range", "$$")}
      </div>
      <div className="space-y-1.5">
        <Label className="text-slate-300 text-sm">Description</Label>
        <Textarea data-testid="biz-description-input" value={f.description} onChange={set("description")} rows={3} className={cls} placeholder="What makes your place a must-visit?" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-slate-300 text-sm">Business photo</Label>
        <PhotoUpload value={f.image_url} onChange={(url) => setF({ ...f, image_url: url })} />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-[#26334D] p-3">
        <Label className="text-slate-300 text-sm">Reservation required?</Label>
        <Switch data-testid="biz-reservation-switch" checked={f.reservation_required} onCheckedChange={set("reservation_required")} />
      </div>
      {f.reservation_required && text("reservation_note", "Reservation note", "Book 24h ahead online")}
      <DealsEditor deals={f.deals || []} onChange={(deals) => setF({ ...f, deals })} />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Show dates & times</Label>
          <Button type="button" data-testid="biz-add-showtime" variant="ghost" size="sm" onClick={() => setF({ ...f, schedule: [...f.schedule, { _key: crypto.randomUUID(), date: "", time: "", title: "" }] })}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        {f.schedule.map((s, i) => (
          <div key={s._key} className="grid grid-cols-[1fr_auto_auto_auto] gap-2">
            <Input data-testid={`biz-show-title-${i}`} placeholder="Show title" value={s.title} onChange={(e) => setSched(i, "title", e.target.value)} className={cls} />
            <Input data-testid={`biz-show-date-${i}`} type="date" required value={s.date} onChange={(e) => setSched(i, "date", e.target.value)} className={`${cls} w-36`} />
            <Input data-testid={`biz-show-time-${i}`} placeholder="8:00 PM" required value={s.time} onChange={(e) => setSched(i, "time", e.target.value)} className={`${cls} w-24`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => setF({ ...f, schedule: f.schedule.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label className="text-slate-300 text-sm">Today's update (shown to travelers)</Label>
        <Input data-testid="biz-daily-update-input" value={f.daily_update} onChange={set("daily_update")} placeholder="Live band tonight 9pm — happy hour till 7!" className={`h-11 ${cls}`} />
      </div>
      <Button data-testid={initial?.id ? "update-hours-submit" : "add-business-submit"} disabled={busy} type="submit"
        className="w-full h-12 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
        {busy ? "Saving…" : initial?.id ? "Save changes" : "Add business"}
      </Button>
    </form>
  );
}
